/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
  LocalOffer as LocalOfferIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { fetchDynamicPrices, saveDynamicPrices } from '../../utils/pricingService';
import { mockProductsData } from '../../mock/productsData';

export default function PriceManagement({ token, showSnack }) {
  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Initialize or load prices from backend API
  const loadPrices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDynamicPrices();
      let list = [];
      if (Array.isArray(data) && data.length > 0) {
        list = data.map((item) => ({
          id: String(item.id),
          name: item.name || '',
          category: item.category || 'CURTAINS',
          price_per_sqft: Number(item.price_per_sqft) || 0,
          offer_percentage: Number(item.offer_percentage) || 0,
        }));
      } else {
        // Fallback to mock data defaults
        list = mockProductsData.products.map((p) => ({
          id: String(p._id),
          name: p.name,
          category: p.category,
          price_per_sqft: Number(p.price_per_sqft) || 0,
          offer_percentage: Number(p.offer_percentage) || 0,
        }));
      }

      setProducts(list);
      setOriginalProducts(JSON.parse(JSON.stringify(list)));
    } catch (err) {
      showSnack('Failed to load prices. Please refresh.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnack]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  // Handle single product field updates
  const handlePriceChange = (id, value) => {
    const num = value === '' ? '' : Math.max(0, parseFloat(value) || 0);
    setProducts((prev) =>
      prev.map((item) => (item.id === id ? { ...item, price_per_sqft: num } : item))
    );
  };

  const handleOfferChange = (id, value) => {
    let num = value === '' ? '' : parseFloat(value) || 0;
    if (num < 0) num = 0;
    if (num > 100) num = 100;
    setProducts((prev) =>
      prev.map((item) => (item.id === id ? { ...item, offer_percentage: num } : item))
    );
  };

  // Detect whether changes exist compared to saved state
  const hasChanges = useMemo(() => {
    if (products.length !== originalProducts.length) return false;
    return products.some((p) => {
      const orig = originalProducts.find((o) => o.id === p.id);
      if (!orig) return false;
      return (
        Number(orig.price_per_sqft) !== Number(p.price_per_sqft) ||
        Number(orig.offer_percentage) !== Number(p.offer_percentage)
      );
    });
  }, [products, originalProducts]);

  // Save all price changes to server
  const handleSaveAll = async () => {
    if (!token) {
      showSnack('Admin session expired. Please log in again.', 'warning');
      return;
    }

    setSaving(true);
    try {
      // Validate that all fields have valid numbers
      const sanitized = products.map((p) => ({
        ...p,
        price_per_sqft: Number(p.price_per_sqft) || 0,
        offer_percentage: Number(p.offer_percentage) || 0,
      }));

      const res = await saveDynamicPrices(token, sanitized);
      if (res.success) {
        setOriginalProducts(JSON.parse(JSON.stringify(sanitized)));
        showSnack('Product prices and offers updated successfully!', 'success');
      }
    } catch (err) {
      showSnack(err.message || 'Error updating prices', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Discard unsaved changes
  const handleDiscardChanges = () => {
    setProducts(JSON.parse(JSON.stringify(originalProducts)));
    showSnack('Unsaved price edits reverted', 'info');
  };

  // Restore factory mock defaults
  const handleRestoreDefaults = () => {
    const defaults = mockProductsData.products.map((p) => ({
      id: String(p._id),
      name: p.name,
      category: p.category,
      price_per_sqft: Number(p.price_per_sqft) || 0,
      offer_percentage: Number(p.offer_percentage) || 0,
    }));
    setProducts(defaults);
    setResetDialogOpen(false);
    showSnack('Restored factory defaults. Click "Save Changes" to apply.', 'warning');
  };

  // Filtered list
  const filteredProducts = useMemo(
    () =>
      products.filter((p) => {
        const matchesSearch =
          searchQuery.trim() === '' ||
          p.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCat =
          categoryFilter === 'all' ||
          p.category.toUpperCase() === categoryFilter.toUpperCase();
        return matchesSearch && matchesCat;
      }),
    [products, searchQuery, categoryFilter]
  );

  // Stats
  const stats = useMemo(() => {
    const total = products.length;
    const curtains = products.filter((p) => p.category.toUpperCase() === 'CURTAINS').length;
    const blinds = products.filter((p) => p.category.toUpperCase() === 'BLINDS').length;
    const withOffers = products.filter((p) => Number(p.offer_percentage) > 0).length;
    return { total, curtains, blinds, withOffers };
  }, [products]);

  return (
    <Box>
      {/* Top Controls & Save Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="#111827">
            Curtain & Blind Price Management
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage per-square-foot prices and promotional offers. Saved instantly to server storage without touching the database.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={loadPrices}
            disabled={loading || saving}
            sx={{ borderRadius: 2 }}
          >
            Reload
          </Button>

          {hasChanges && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleDiscardChanges}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              Discard Changes
            </Button>
          )}

          <Button
            variant="contained"
            color="error"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={saving || !hasChanges}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 700,
              backgroundColor: '#ec2927',
              '&:hover': { backgroundColor: '#d02524' },
            }}
          >
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </Box>
      </Paper>

      {/* Unsaved Changes Banner */}
      {hasChanges && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2, border: '1px solid #FFE2B7' }}
          action={
            <Button color="inherit" size="small" onClick={handleSaveAll} disabled={saving}>
              Save Now
            </Button>
          }
        >
          You have unsaved price changes. Click <b>&quot;Save All Changes&quot;</b> to apply them to the live estimate calculator.
        </Alert>
      )}

      {/* Metric Cards */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2.5,
                  backgroundColor: '#FEECEB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ec2927',
                }}
              >
                <LocalOfferIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} color="#111827">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Products
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2.5,
                  backgroundColor: '#E8F4FD',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1877F2',
                }}
              >
                <Typography fontWeight={800} fontSize="18px">
                  C
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} color="#111827">
                  {stats.curtains}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Curtain Types
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2.5,
                  backgroundColor: '#F3E8FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9333EA',
                }}
              >
                <Typography fontWeight={800} fontSize="18px">
                  B
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} color="#111827">
                  {stats.blinds}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Blind Types
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2.5,
                  backgroundColor: '#EBF8F2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1B8057',
                }}
              >
                <TrendingDownIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} color="#111827">
                  {stats.withOffers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Discounts
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filter Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" gap={2} width={{ xs: '100%', sm: 'auto' }} flex={1} maxWidth={600}>
          <TextField
            size="small"
            placeholder="Search curtain or blind name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="CURTAINS">Curtains Only</MenuItem>
              <MenuItem value="BLINDS">Blinds Only</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Button
          size="small"
          color="inherit"
          startIcon={<RestartAltIcon />}
          onClick={() => setResetDialogOpen(true)}
          sx={{ textTransform: 'none', color: '#6B7280' }}
        >
          Reset to Factory Defaults
        </Button>
      </Paper>

      {/* Editable Pricing Table */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#374151', width: '30%' }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', width: '15%' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', width: '20%' }}>
                  Base Price (AED / sq.ft)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', width: '15%' }}>
                  Offer Discount (%)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', width: '20%' }}>
                  Live Customer Price
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} color="error" />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Loading prices...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No matching products found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((p) => {
                  const orig = originalProducts.find((o) => o.id === p.id);
                  const isDirty =
                    orig &&
                    (Number(orig.price_per_sqft) !== Number(p.price_per_sqft) ||
                      Number(orig.offer_percentage) !== Number(p.offer_percentage));

                  const priceNum = Number(p.price_per_sqft) || 0;
                  const offerNum = Number(p.offer_percentage) || 0;
                  const discountedPrice = Math.max(0, priceNum - (priceNum * offerNum) / 100);

                  return (
                    <TableRow
                      key={p.id}
                      hover
                      sx={{
                        backgroundColor: isDirty ? '#FFFBEB' : 'inherit',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {/* Product Name */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight={600} color="#111827">
                            {p.name}
                          </Typography>
                          {isDirty && (
                            <Chip
                              label="Modified"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '11px',
                                backgroundColor: '#FEF3C7',
                                color: '#92400E',
                                fontWeight: 700,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Chip
                          label={p.category}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '11px',
                            backgroundColor:
                              p.category.toUpperCase() === 'CURTAINS' ? '#FEECEB' : '#E8F4FD',
                            color:
                              p.category.toUpperCase() === 'CURTAINS' ? '#ec2927' : '#1877F2',
                          }}
                        />
                      </TableCell>

                      {/* Base Price input */}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={p.price_per_sqft}
                          onChange={(e) => handlePriceChange(p.id, e.target.value)}
                          inputProps={{ min: 0, step: '1' }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                  AED
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            maxWidth: 160,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#ffffff',
                              borderRadius: 1.5,
                              fontWeight: 600,
                            },
                          }}
                        />
                      </TableCell>

                      {/* Offer Percentage input */}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={p.offer_percentage}
                          onChange={(e) => handleOfferChange(p.id, e.target.value)}
                          inputProps={{ min: 0, max: 100, step: '1' }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                  %
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            maxWidth: 120,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#ffffff',
                              borderRadius: 1.5,
                              fontWeight: 600,
                            },
                          }}
                        />
                      </TableCell>

                      {/* Live Customer Price */}
                      <TableCell>
                        <Box>
                          <Typography fontWeight={700} color="#111827">
                            AED {discountedPrice.toFixed(2)}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              / sq.ft
                            </Typography>
                          </Typography>

                          {offerNum > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              <s>AED {priceNum.toFixed(2)}</s>{' '}
                              <Typography component="span" variant="caption" color="success.main" fontWeight={700}>
                                (-{offerNum}%)
                              </Typography>
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Confirmation Dialog to Reset to Defaults */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset to Factory Defaults?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reset all 10 product prices and discounts back to initial default values.
            You will still need to click &quot;Save All Changes&quot; to apply them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleRestoreDefaults} color="error" variant="contained">
            Reset Defaults
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

PriceManagement.propTypes = {
  token: PropTypes.string.isRequired,
  showSnack: PropTypes.func.isRequired,
};
